import type React from "react";
import { Navigate, useHref, useLocation } from "react-router";
import { i18n } from "@frontend/i18n";
import { useUserActions } from "@frontend/shared/features/actions/application/actions/use-user-actions";
import type { AuthenticatedRouteGuardProps } from "./route.types";

const DEFAULT_LOGIN_PATH = "/login";
const DEFAULT_UNAUTHORIZED_PATH = "/unauthorized";

export function AuthLoadingSpinner(): React.JSX.Element {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div
          className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary"
          role="status"
          aria-label={i18n.t("common.loading")}
        />
        <span className="text-sm text-muted-foreground">
          {i18n.t("common.loading")}
        </span>
      </div>
    </div>
  );
}

export function AuthenticatedRouteGuard({
  children,
  requiredUserRoles,
  unauthorizedFallback,
  loadingFallback,
  errorFallback,
}: AuthenticatedRouteGuardProps): React.JSX.Element {
  const {
    user: currentUser,
    isLoading,
    isAuthenticated,
    error,
  } = useUserActions();

  // The router location, not window.location: after a redirect the browser URL
  // is already the login page while this tree can still re-render once.
  // useHref includes the basename, which returnUrl needs for the full-page navigation after signin;
  // the Navigate target must not include it, because the router prepends it.
  const returnUrl = useHref(useLocation());
  const loginUrlWithReturn = `${DEFAULT_LOGIN_PATH}?returnUrl=${encodeURIComponent(returnUrl)}`;

  if (isLoading) {
    if (loadingFallback !== undefined) {
      return <>{loadingFallback}</>;
    }
    return <AuthLoadingSpinner />;
  }

  if (error !== null) {
    if (errorFallback !== undefined) {
      return <>{errorFallback}</>;
    }
    return <Navigate to={loginUrlWithReturn} replace />;
  }

  if (!isAuthenticated) {
    return <Navigate to={loginUrlWithReturn} replace />;
  }

  if (requiredUserRoles !== undefined && requiredUserRoles.length > 0) {
    const userRoles = currentUser?.$roles ?? [];
    const hasRequiredRole = requiredUserRoles.some((required) =>
      userRoles.includes(required),
    );

    if (!hasRequiredRole) {
      if (unauthorizedFallback !== undefined) {
        return <>{unauthorizedFallback}</>;
      }
      return <Navigate to={DEFAULT_UNAUTHORIZED_PATH} replace />;
    }
  }

  return <>{children}</>;
}
